import { BaseSyntheticEvent, ReactNode, useMemo } from 'react';
import { FieldPath, FieldValues, useForm as useFormRHF } from 'react-hook-form';
import { FieldSchema } from '@/forms/types/field-schema';
import { FormSchema } from '@/forms/types/schema';
import { renderFieldItem } from './render-field-item';

export type UseFormProps<TFieldValues extends FieldValues = FieldValues> = {
  schema: FormSchema<TFieldValues>;
  onSubmit: (values: TFieldValues) => void;
};

export type UseFormReturn<TFieldValues extends FieldValues = FieldValues> = {
  fieldItems: ReactNode[];
  handleSubmit: (e?: BaseSyntheticEvent) => Promise<void>;
} & Pick<
  ReturnType<typeof useFormRHF<TFieldValues>>,
  'formState' | 'trigger' | 'watch'
>;

export const useForm = <TFieldValues extends FieldValues = FieldValues>({
  schema,
  onSubmit,
}: UseFormProps<TFieldValues>): UseFormReturn<TFieldValues> => {
  const { control, formState, handleSubmit, trigger, watch } =
    useFormRHF<TFieldValues>();

  const fieldItems = useMemo(
    () =>
      Object.entries<FieldSchema<TFieldValues>>(schema).map(([key, field]) =>
        renderFieldItem<TFieldValues>(
          key as FieldPath<TFieldValues>,
          field,
          control,
        ),
      ),
    [control, schema],
  );

  return {
    fieldItems,
    formState,
    handleSubmit: handleSubmit(onSubmit),
    trigger,
    watch,
  };
};
